// src/web/videoBackground.js
//
// FRAME-LOCKED video background. Fixes the "glasses lag the face" problem.
//
// ROOT CAUSE: previously the live <video> element was shown as the page
// background with a transparent canvas on top. The browser composites the
// <video> in real time, but the glasses are computed from a frame we grabbed and
// ran through MediaPipe (~10-20ms inference) before rendering. So the displayed
// face is "now" and the glasses are from "a few ms ago" → during motion the
// glasses trail the face by 1-3 frames, no matter how little we smooth. Smoothing
// can't fix this; it's a synchronisation gap.
//
// FIX: stop showing the live <video>. Instead, at the SAME instant we run
// detection, copy that exact video frame into a texture and draw it as the scene
// background. Now the displayed face and the glasses come from the SAME frame, so
// they are locked together and the glasses "stick" — even with zero smoothing.
//
// Implementation: a full-viewport quad in a separate orthographic scene, textured
// from the video, drawn before the 3D glasses each frame. captureFrame() pulls the
// current video pixels at detection time.

import {
  Scene, OrthographicCamera, PlaneGeometry, Mesh, MeshBasicMaterial,
  CanvasTexture, LinearFilter, SRGBColorSpace
} from 'three';

export class VideoBackground {
  constructor(videoEl) {
    this.video = videoEl;

    // Offscreen canvas we copy each detected video frame into.
    this._canvas = document.createElement('canvas');
    this._ctx = this._canvas.getContext('2d', { willReadFrequently: false });

    this._tex = new CanvasTexture(this._canvas);
    this._tex.minFilter = LinearFilter;
    this._tex.magFilter = LinearFilter;
    // Match the renderer's output color space so the camera image isn't shifted
    // (washed out / wrong gamma) when drawn through the WebGL pipeline.
    this._tex.colorSpace = SRGBColorSpace;

    this.scene = new Scene();
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geo = new PlaneGeometry(2, 2);
    const mat = new MeshBasicMaterial({
      map: this._tex,
      depthWrite: false,
      depthTest: false,
      toneMapped: false   // the camera image must NOT be ACES tone-mapped (would
                          // wash out / shift its colors). Render it as-is.
    });
    this.mesh = new Mesh(geo, mat);
    this.scene.add(this.mesh);

    this._ready = false;
  }

  /**
   * Copy the CURRENT video frame into the background texture. Call this in the
   * render loop right after (or before) detection, using the same frame, so the
   * displayed image is locked to the detected landmarks.
   */
  /** The offscreen canvas holding the last captured frame (detection source). */
  get canvas() { return this._canvas; }

  captureFrame() {
    const v = this.video;
    const w = v.videoWidth, h = v.videoHeight;
    if (!w || !h) return false;
    if (this._canvas.width !== w || this._canvas.height !== h) {
      this._canvas.width = w;
      this._canvas.height = h;
    }
    // Draw the video frame as-is (no mirroring here; the canvas element is
    // CSS-mirrored to match, same as before).
    this._ctx.drawImage(v, 0, 0, w, h);
    this._tex.needsUpdate = true;
    this._ready = true;
    return true;
  }

  /**
   * Render the background quad. Call before rendering the 3D scene, with
   * renderer.autoClear handled by the caller (clear once, then render bg, then
   * render 3D without clearing).
   */
  render(renderer) {
    if (!this._ready) return;
    renderer.render(this.scene, this.camera);
  }

  dispose() {
    this._tex.dispose();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
